// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useContext, useState } from 'react';
import { Checkbox, Form, FormRow, InputError, ValidInput, PopupHint, CheckboxGroup, Alert } from '@op/opds.opux';
import { CompanyData, ContextWrapper } from '../../context/ContextWrapper';
import {
    isValidBusinessId,
    isValidPhoneInput,
    isValidPostalCodeInput,
    isValidTextInput,
    isValidCompanyType,
} from '../../validation/Validation';
import message from '../../messages';
import { companyTypes, id } from '../../Constants';
import { deepCopy, sortByAgreementType } from '../../util/Util';
import DigitalAgreementInformation from './DigitalAgreementInformation';
import AgreementTypesInfoSelection from './AgreementTypesInfoSelection';

interface CompanyTypeSelected {
    isSelected: boolean;
    showWarning?: boolean;
}

interface Admin {
    firstName: string;
    agreementTypes: Record<string, any>;
}

const CompanyForm: React.FC = () => {
    const { contractData, updateInformation, isAdminFlow } = useContext(ContextWrapper);
    const [analyticsActivated, setAnalyticsActivated] = useState<boolean>(false);

    const modifyState = (variable: string, data: any): void => {
        updateInformation((state: any) => {
            return { ...state, [variable]: data };
        });
    };

    const data = deepCopy(contractData);
    const companyTypeSelected: CompanyTypeSelected = { ...data.companyTypeSelected };
    const companyData: CompanyData = { ...data.companyData };
    const checkValidation: boolean = data.checkValidation;
    const administrators: Admin[] = [...data.administrators];

    const countryValidation = isValidTextInput(companyData.country as string, checkValidation),
        businessIdValidation = isValidBusinessId(companyData.businessId, checkValidation),
        companyNameValidation = isValidTextInput(companyData.companyName, checkValidation),
        streetAddressValidation = isValidTextInput(companyData.streetAddress as string, checkValidation),
        postalCodeValidation = isValidPostalCodeInput(companyData.postalCode as string, checkValidation),
        cityTownValidation = isValidTextInput(companyData.cityTown as string, checkValidation),
        phoneValidation = isValidPhoneInput(companyData.phone as string, checkValidation),
        companyTypeValidation = isValidCompanyType(
            companyData.companyTypes,
            contractData.showAgreementTypes,
            checkValidation
        );

    const removeAllRoles = (companyType: string): Admin[] => {
        return administrators.map((admin) => {
            const copyOfAdmin = deepCopy(admin);
            if (copyOfAdmin.agreementTypes[companyType]) {
                delete copyOfAdmin.agreementTypes[companyType];
            }
            return copyOfAdmin;
        });
    };

    const shouldShowWarning = (selectedValue: string): void => {
        if (
            !companyData.companyTypes.includes(selectedValue) &&
            companyTypeSelected.isSelected &&
            administrators[0].firstName !== ''
        ) {
            companyTypeSelected.showWarning = true;
        }
    };

    const showCompanyTypeChangeWarning = (): React.ReactElement | undefined => {
        if (companyTypeSelected.showWarning === true) {
            return (
                <div className="alert-info-wrapper" data-testid="companyTypeAlert">
                    <Alert id="companyTypeChange" data-testid="companyTypeChange">
                        {message('agreementTypeChangeWarning')}
                    </Alert>
                </div>
            );
        }
    };

    const activateAnalytics = (isAdminFlow: boolean): void => {
        if (!isAdminFlow && !analyticsActivated) {
            try {
                let language = document.documentElement.getAttribute('lang');
                if (window._satellite) {
                    window._satellite.track('application_phase', {
                        phase_category: 'start',
                        name: 'vv ya verkkopalvelusopimus',
                        group: 'self-service',
                        business_group: 'insurance',
                        channel: 'ya',
                        phase: 'lomake aloitettu',
                        language: { language }, //valid: fi_fi en_us sv_se
                    });
                }
                setAnalyticsActivated(true);
                // eslint-disable-next-line
            } catch (e) {
                // Fail silently, tracking code no error
            }
        }
    };

    const updateCompanyTypes = (type: string, checked: boolean): void => {
        const companyTypes = companyData.companyTypes;
        if (checked) {
            shouldShowWarning(type);
            companyTypes.push(type);
            companyData.companyTypes = companyTypes.toSorted(sortByAgreementType);
        } else {
            companyData.companyTypes = companyTypes.filter((companyType) => companyType !== type);
            const admins = removeAllRoles(type);
            modifyState('administrators', admins);
        }
        companyTypeSelected.isSelected = companyTypes.length > 0;
        modifyState('companyData', companyData);
        modifyState('companyTypeSelected', companyTypeSelected);
    };

    return (
        <Form visualizeRequiredFields className="opux-spacing-bottom-margin-double" data-testid="companyForm">
            <div className="opux-spacing-bottom-margin-double heading-with-popup-hint">
                <h2 id="companyInformationHeader" data-testid="companyInformationHeader">
                    {message('companyInformation')}
                </h2>
                {!isAdminFlow && (
                    <PopupHint
                        label={message('companyInformationPopupLabel')}
                        className="agreement-type-info"
                        data-testid="popUpHint"
                    >
                        {message('companyInformationInfoBox')}
                    </PopupHint>
                )}
            </div>
            <FormRow label={message('businessId')} labelFor="companyBusinessId" required data-testid="businessIdRow">
                <ValidInput
                    id={id.BUSINESS_ID}
                    value={companyData.businessId}
                    error={businessIdValidation.message}
                    invalid={businessIdValidation.notValid}
                    maxLength={9}
                    onChange={(event: any) => {
                        companyData.businessId = event.target.value;
                        modifyState('companyData', companyData);
                    }}
                    data-testid="businessIdInput"
                    onFocus={() => activateAnalytics(isAdminFlow)}
                />
            </FormRow>
            <FormRow label={message('companyName')} labelFor="companyName" required data-testid="companyNameRow">
                <ValidInput
                    id={id.COMPANY_NAME}
                    value={companyData.companyName}
                    error={companyNameValidation.message}
                    invalid={isAdminFlow ? businessIdValidation.notValid : companyNameValidation.notValid}
                    maxLength={50}
                    onChange={(event: any) => {
                        companyData.companyName = event.target.value;
                        modifyState('companyData', companyData);
                    }}
                    data-testid="companyNameInput"
                />
            </FormRow>
            {!isAdminFlow && (
                <FormRow
                    label={message('streetAddress')}
                    labelFor="streetAddress"
                    required
                    data-testid="streetAddressRow"
                >
                    <ValidInput
                        id={id.STREET_ADDRESS}
                        value={companyData.streetAddress as string}
                        error={streetAddressValidation.message}
                        invalid={streetAddressValidation.notValid}
                        maxLength={100}
                        onChange={(event: any) => {
                            companyData.streetAddress = event.target.value;
                            modifyState('companyData', companyData);
                        }}
                        data-testid="streetAddressInput"
                    />
                </FormRow>
            )}
            {!isAdminFlow && (
                <FormRow label={message('postalCode')} labelFor="postalCode" required data-testid="postalCodeRow">
                    <ValidInput
                        id={id.POSTAL_CODE}
                        value={companyData.postalCode as string}
                        error={message(postalCodeValidation.message)}
                        invalid={postalCodeValidation.notValid}
                        maxLength={5}
                        onChange={(event: any) => {
                            companyData.postalCode = event.target.value;
                            modifyState('companyData', companyData);
                        }}
                        data-testid="postalCodeInput"
                    />
                </FormRow>
            )}
            {!isAdminFlow && (
                <FormRow label={message('cityTown')} labelFor="cityTown" required data-testid="cityTownRow">
                    <ValidInput
                        id={id.CITY_TOWN}
                        value={companyData.cityTown as string}
                        error={cityTownValidation.message}
                        invalid={cityTownValidation.notValid}
                        maxLength={200}
                        onChange={(event: any) => {
                            companyData.cityTown = event.target.value;
                            modifyState('companyData', companyData);
                        }}
                        data-testid="cityTownInput"
                    />
                </FormRow>
            )}
            {!isAdminFlow && (
                <FormRow
                    hint={<PopupHint>{message('phoneInfoBox')}</PopupHint>}
                    label={message('phone')}
                    labelFor="phone"
                    required
                    data-testid="phoneRow"
                >
                    <ValidInput
                        type="tel"
                        id={id.PHONE}
                        value={companyData.phone as string}
                        error={phoneValidation.message}
                        invalid={phoneValidation.notValid}
                        maxLength={20}
                        onChange={(event: any) => {
                            companyData.phone = event.target.value;
                            modifyState('companyData', companyData);
                        }}
                        data-testid="phoneInput"
                    />
                </FormRow>
            )}
            {!isAdminFlow && (
                <FormRow data-testid="isForeignCompanyRow">
                    <Checkbox
                        aria-label={message('isForeignCompanyMessage')}
                        id={id.IS_FOREIGN_COMPANY}
                        size="small"
                        checked={companyData.isForeignCompany}
                        onChange={(_env: any, checked: boolean) => {
                            companyData.isForeignCompany = checked;
                            modifyState('companyData', companyData);
                        }}
                        data-testid="isForeignCompanyCheckbox"
                    >
                        <div aria-hidden>{message('isForeignCompanyMessage')}</div>
                    </Checkbox>
                </FormRow>
            )}
            {companyData.isForeignCompany && (
                <FormRow label={message('country')} labelFor="country" data-testid="countryRow">
                    <ValidInput
                        id={id.COUNTRY}
                        value={companyData.country as string}
                        error={countryValidation.message}
                        invalid={countryValidation.notValid}
                        maxLength={50}
                        onChange={(event: any) => {
                            companyData.country = event.target.value;
                            modifyState('companyData', companyData);
                        }}
                        data-testid="countryInput"
                    />
                </FormRow>
            )}
            <AgreementTypesInfoSelection />
            {!isAdminFlow && contractData.showAgreementTypes && <DigitalAgreementInformation />}
            {!isAdminFlow && contractData.showAgreementTypes && (
                <>
                    <CheckboxGroup size="small" data-testid="companyTypesCheckboxGroup">
                        {companyTypes.map((type) => (
                            <Checkbox
                                id={type}
                                aria-label={message(type)}
                                key={type}
                                onChange={(_: any, checked: boolean) => updateCompanyTypes(type, checked)}
                                checked={companyData.companyTypes.includes(type)}
                                invalid={companyTypeValidation.notValid}
                                data-testid={`companyTypeCheckbox-${type}`}
                            >
                                <div aria-hidden data-testid="companyTypeName">
                                    {message(type)}
                                </div>
                                <PopupHint className="agreement-type-info" data-testid="popUpHint">
                                    {message(`${type}InfoBox`)}
                                </PopupHint>
                            </Checkbox>
                        ))}
                    </CheckboxGroup>
                    <InputError error={companyTypeValidation.message} data-testid="companyTypeError" />
                    {showCompanyTypeChangeWarning()}
                </>
            )}
        </Form>
    );
};

export default CompanyForm;
